import { useState } from 'react';

/** Enough of JSON Schema to build a form for typical MCP tools; anything else falls back to raw JSON. */
interface Prop {
  type?: string | string[];
  description?: string;
  enum?: unknown[];
  default?: unknown;
}
interface ObjectSchema {
  type?: string;
  properties?: Record<string, Prop>;
  required?: string[];
}

const simpleType = (p: Prop): 'string' | 'number' | 'boolean' | 'enum' | 'json' => {
  if (Array.isArray(p.enum)) return 'enum';
  const t = Array.isArray(p.type) ? p.type.find((x) => x !== 'null') : p.type;
  if (t === 'string') return 'string';
  if (t === 'number' || t === 'integer') return 'number';
  if (t === 'boolean') return 'boolean';
  return 'json';
};

export function ToolForm({
  schema,
  initial,
  busy,
  submitLabel,
  onSubmit,
}: {
  schema: unknown;
  initial?: Record<string, unknown>;
  busy?: boolean;
  submitLabel: string;
  onSubmit: (args: Record<string, unknown>) => void;
}) {
  const s = (schema ?? {}) as ObjectSchema;
  const props = s.properties ?? {};
  const required = new Set(s.required ?? []);
  const names = Object.keys(props);
  const [raw, setRaw] = useState(() => JSON.stringify(initial ?? {}, null, 2));
  const [values, setValues] = useState<Record<string, string>>(() => {
    const v: Record<string, string> = {};
    for (const n of names) {
      const init = initial?.[n] ?? props[n]?.default;
      if (init === undefined) continue;
      v[n] = typeof init === 'string' ? init : JSON.stringify(init);
    }
    return v;
  });
  const [useRaw, setUseRaw] = useState(names.length === 0);
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    try {
      if (useRaw) {
        const parsed = JSON.parse(raw || '{}');
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed))
          throw new Error('arguments must be a JSON object');
        onSubmit(parsed as Record<string, unknown>);
        return;
      }
      const args: Record<string, unknown> = {};
      for (const n of names) {
        const p = props[n];
        if (!p) continue;
        const v = values[n];
        if (v === undefined || v === '') {
          if (required.has(n)) throw new Error(`${n} is required`);
          continue;
        }
        switch (simpleType(p)) {
          case 'string':
          case 'enum':
            args[n] = v;
            break;
          case 'number': {
            const num = Number(v);
            if (Number.isNaN(num)) throw new Error(`${n} must be a number`);
            args[n] = num;
            break;
          }
          case 'boolean':
            args[n] = v === 'true';
            break;
          default:
            args[n] = JSON.parse(v);
        }
      }
      onSubmit(args);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="stack">
      {!useRaw &&
        names.map((n) => {
          const p = props[n] as Prop;
          const kind = simpleType(p);
          const v = values[n] ?? '';
          const set = (x: string) => setValues((prev) => ({ ...prev, [n]: x }));
          const inputId = `tf-${n}`;
          return (
            <label className="field" key={n} htmlFor={inputId}>
              <span>
                <code>{n}</code>
                {required.has(n) ? ' *' : ''} {p.description ? `— ${p.description}` : ''}
              </span>
              {kind === 'enum' ? (
                <select id={inputId} value={v} onChange={(e) => set(e.target.value)}>
                  <option value="">—</option>
                  {(p.enum ?? []).map((o) => (
                    <option key={String(o)} value={String(o)}>
                      {String(o)}
                    </option>
                  ))}
                </select>
              ) : kind === 'boolean' ? (
                <select id={inputId} value={v} onChange={(e) => set(e.target.value)}>
                  <option value="">—</option>
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              ) : kind === 'json' ? (
                <textarea
                  id={inputId}
                  value={v}
                  onChange={(e) => set(e.target.value)}
                  placeholder="JSON"
                />
              ) : (
                <input
                  id={inputId}
                  value={v}
                  inputMode={kind === 'number' ? 'decimal' : 'text'}
                  onChange={(e) => set(e.target.value)}
                />
              )}
            </label>
          );
        })}
      {useRaw && (
        <label className="field">
          <span>Arguments (JSON)</span>
          <textarea rows={6} value={raw} onChange={(e) => setRaw(e.target.value)} />
        </label>
      )}
      {error && <p className="error">{error}</p>}
      <div className="row">
        <button type="button" className="primary" disabled={busy} onClick={submit}>
          {submitLabel}
        </button>
        {names.length > 0 && (
          <button type="button" className="small" onClick={() => setUseRaw((x) => !x)}>
            {useRaw ? 'Use form' : 'Edit as JSON'}
          </button>
        )}
      </div>
    </div>
  );
}
